/**
 * Helper functions for managing portal URL parameters
 */

import { isFamilyPagePath } from "@marble/core/modules/students/family-links/paths";

export interface PortalParams {
  userId?: string;
  therapistId?: string;
  type?: string;
  /**
   * A family page to return to after signing in (an old or expired family
   * link sends the family here). Honored only if it is one.
   */
  next?: string;
}

/**
 * Builds a URLSearchParams object from portal parameters
 */
export function buildPortalParams(params: PortalParams): URLSearchParams {
  const urlParams = new URLSearchParams();

  if (params.userId) {
    urlParams.set("userId", params.userId);
  }
  if (params.therapistId) {
    urlParams.set("therapistId", params.therapistId);
  }
  if (params.type) {
    urlParams.set("type", params.type);
  }
  if (isFamilyPagePath(params.next)) {
    urlParams.set("next", params.next as string);
  }

  return urlParams;
}

/**
 * Determines the appropriate redirect URL based on portal parameters
 */
export function getPortalRedirectUrl(
  params: PortalParams,
  fromScheduleRoute: boolean = false,
): string {
  if (isFamilyPagePath(params.next)) {
    return params.next as string;
  }

  // Redirect to schedule page if:
  // 1. user was on /portal/schedule route, OR
  // 2. therapistId or type parameters are present (indicating scheduling context)
  const shouldRedirectToSchedule =
    fromScheduleRoute || params.therapistId || params.type;
  const baseUrl = shouldRedirectToSchedule
    ? "/portal/schedule"
    : "/portal/appointments";
  const urlParams = buildPortalParams(params);

  return urlParams.toString() ? `${baseUrl}?${urlParams.toString()}` : baseUrl;
}

/**
 * Builds a login URL with portal parameters
 */
export function getPortalLoginUrl(params: PortalParams): string {
  const urlParams = buildPortalParams(params);
  return urlParams.toString()
    ? `/portal/login?${urlParams.toString()}`
    : "/portal/login";
}
